import React, { forwardRef } from 'react';
import classNames from 'classnames';
import { useMergeRefs } from '@floating-ui/react';

import {
  Icon,
  Heading,
  Comp,
  getStylingTransientProps,
  getFlexLayoutTransientProps,
  useId,
  Skeleton,
} from '@redsift/design-system';
import { StyledDialogContentHeader } from './styles';
import { DialogContentHeaderProps } from './types';
import { useDialogContext } from '../dialog';

const COMPONENT_NAME = 'DialogContentHeader';
const CLASSNAME = 'redsift-dialog-content-header';

/**
 * The DialogContentHeader component.
 */
export const DialogContentHeader: Comp<DialogContentHeaderProps, HTMLDivElement> = forwardRef((props, ref) => {
  const propsWithDefaults = {
    flexDirection: 'column' as const,
    ...props,
  };
  const { transientProps: stylingTransientProps, otherProps: propsWithoutStyling } =
    getStylingTransientProps(propsWithDefaults);
  const { transientProps: flexTransientProps, otherProps } = getFlexLayoutTransientProps(propsWithoutStyling);
  const transientProps = { ...stylingTransientProps, ...flexTransientProps };

  const { children, className, header, headingProps, icon, iconProps, isLoading, subheader, theme, ...forwardedProps } =
    otherProps;

  const { setLabelId, setDescriptionId, headerRef, initialFocus } = useDialogContext();
  const [labelId] = useId();
  const [descriptionId] = useId();

  // Only sets `aria-labelledby` on the Dialog root element
  // if this component is mounted inside it.
  React.useLayoutEffect(() => {
    setLabelId(labelId);
    return () => setLabelId(undefined);
  }, [labelId, setLabelId]);

  // Only sets `aria-describedby` on the Dialog root element
  // if this component is mounted inside it.
  React.useLayoutEffect(() => {
    setDescriptionId(descriptionId);
    return () => setDescriptionId(undefined);
  }, [descriptionId, setDescriptionId]);

  return (
    <StyledDialogContentHeader
      {...forwardedProps}
      {...transientProps}
      className={classNames(DialogContentHeader.className, className)}
      ref={useMergeRefs([ref, headerRef])}
      tabIndex={initialFocus === 'header' ? -1 : undefined}
    >
      {subheader ? (
        <Skeleton.Text theme={theme} variant="body" isLoaded={!isLoading}>
          <div className={`${DialogContentHeader.className}__subheader`}>{subheader}</div>
        </Skeleton.Text>
      ) : null}
      {header ? (
        <Skeleton.Text
          theme={theme}
          variant={headingProps?.variant || 'h2'}
          isLoaded={!isLoading}
          marginTop="10px"
          marginBottom="10px"
        >
          <Heading
            theme={theme}
            as="h2"
            color="black"
            className={`${DialogContentHeader.className}__header`}
            variant="h2"
            {...headingProps}
          >
            {icon ? <Icon theme={theme} icon={icon} aria-hidden="true" size="large" {...iconProps} /> : null}
            {header}
          </Heading>
        </Skeleton.Text>
      ) : null}
      {children ? <div id={descriptionId}>{children}</div> : null}
    </StyledDialogContentHeader>
  );
});
DialogContentHeader.className = CLASSNAME;
DialogContentHeader.displayName = COMPONENT_NAME;
